import * as z from 'zod';
import type { useIntl } from 'react-intl';
import { errorMessages } from './messages';

export interface TiltFormSchema {
  // this is our connection with the backend
  numberOfPeople: string; // this is a string because it is a select option ie. '0' or '1'
  numberOfChildren: string; // this is a string because it is a select option ie. '0' or '1'
  monthlyCosts: number;
  typeOfPropertyOwnership?: 'rental' | 'ownership';
  loanFromFamily: number;
  studentLoan: number;
  otherLoans: number;
  monthlyLoanCosts: number;
  monthlyIncome: number;
  savingsRetirementQuestion?: 'yes' | 'no';
  automobileQuestion?: 'yes' | 'no';
  automobileFollowUpQuestion?: 'yes' | 'no';
  childDayCareQuestion?: 'yes' | 'no';
  creditContractQuestion?: 'yes' | 'no';
  wontDiscloseInformation: boolean; // this is tells if cancel form
}

// I need these options for mapping the select options
export const numberOfPeopleOptions = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
] as const;
export const numberOfChildrenOptions = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

// errors even if the value is empty string
const requiredNumber = (formatMessage: FormatMessageType) =>
  z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({
        invalid_type_error: formatMessage(errorMessages.invalidNumber),
        required_error: formatMessage(errorMessages.requiredValue),
      })
      .min(0, { message: formatMessage(errorMessages.minValue) }),
  );

const enumErrorObjectRequired = (formatMessage: FormatMessageType) => ({
  invalid_type_error: formatMessage(errorMessages.invalidEnumValue),
  required_error: formatMessage(errorMessages.requiredValue),
});
const enumErrorObjectOptional = (formatMessage: FormatMessageType) => ({
  invalid_type_error: formatMessage(errorMessages.invalidEnumValue),
});
// schema for entire First Step of Form
export const creditCheckStepOneFormSchema = (formatMessage: FormatMessageType) =>
  z
    .object({
      numberOfPeople: z.enum(numberOfPeopleOptions, enumErrorObjectRequired(formatMessage)),
      numberOfChildren: z.enum(numberOfChildrenOptions, enumErrorObjectRequired(formatMessage)),
      monthlyCosts: requiredNumber(formatMessage),
      typeOfPropertyOwnership: z
        .enum(['rental', 'ownership'], enumErrorObjectOptional(formatMessage))
        .optional(),
      loanFromFamily: requiredNumber(formatMessage),
      studentLoan: requiredNumber(formatMessage),
      otherLoans: requiredNumber(formatMessage),
      monthlyLoanCosts: requiredNumber(formatMessage),
    })
    .refine(
      (data) => {
        // this looks for the first option which will say living with parents
        if (data.numberOfPeople !== '0' && data.typeOfPropertyOwnership === undefined) {
          return false;
        }
        return true;
      },
      {
        message: formatMessage(errorMessages.requiredValue),
        path: ['typeOfPropertyOwnership'],
      },
    );

// schema for Second Step of Form
const creditCheckStepTwoFormSchema = (formatMessage: FormatMessageType) =>
  z
    .object({
      monthlyIncome: requiredNumber(formatMessage),
      savingsRetirementQuestion: z.enum(['yes', 'no'], enumErrorObjectRequired(formatMessage)),
      automobileQuestion: z.enum(['yes', 'no'], enumErrorObjectRequired(formatMessage)),
      automobileFollowUpQuestion: z
        .enum(['yes', 'no'], enumErrorObjectOptional(formatMessage))
        .optional(),
      childDayCareQuestion: z
        .enum(['yes', 'no'], enumErrorObjectOptional(formatMessage))
        .optional(),
    })
    .refine(
      (data) => {
        if (data.automobileQuestion === 'yes' && !data.automobileFollowUpQuestion) {
          return false;
        }
        return true;
      },
      {
        message: formatMessage(errorMessages.requiredValue),
        path: ['automobileFollowUpQuestion'],
      },
    );
export const creditContractQuestionSchema = (formatMessage: FormatMessageType) =>
  z
    .object({
      creditContractQuestion: z.enum(['yes', 'no'], enumErrorObjectRequired(formatMessage)),
      creditInformationChecked: z
        .boolean({ required_error: formatMessage(errorMessages.requiredValue) })
        .optional(),
    })
    .refine(
      (data) => {
        if (data.creditContractQuestion === 'no' && !data.creditInformationChecked) {
          return false;
        }
        return true;
      },
      {
        message: formatMessage(errorMessages.requiredValue),
        path: ['creditInformationChecked'],
      },
    );
export const creditCheckWontDiscloseInformationSchema = (formatMessage: FormatMessageType) =>
  z.boolean({
    required_error: formatMessage(errorMessages.requiredValue),
  });

export type Kind = 'short' | 'long';

// schema for the entire form implicitly sets the kind to long
export const creditCheckFormSchema = <T extends Kind>(formatMessage: FormatMessageType, kind: T) =>
  z
    .object({
      firstPartForm: creditCheckStepOneFormSchema(formatMessage),
      secondPartForm: creditCheckStepTwoFormSchema(formatMessage),
      thirdPartForm: creditContractQuestionSchema(formatMessage),
      wontDiscloseInformation: creditCheckWontDiscloseInformationSchema(formatMessage),
    })
    .refine(
      (data) => {
        if (kind === 'short') {
          // we are in short form childDayCareQuestion is not required
          return true;
        }
        if (data.secondPartForm?.childDayCareQuestion !== undefined) {
          // we are in long form and have answered the question
          return true;
        }
        if (data.firstPartForm.numberOfChildren === '0') {
          // we are in long form and have no children
          return true;
        }
        // we are in long form and have children but have not answered the question
        return false;
      },
      {
        message: formatMessage(errorMessages.requiredValue),
        path: ['secondPartForm.childDayCareQuestion'],
      },
    );
// types section for everything relating to the form
export type FormatMessageType = ReturnType<typeof useIntl>['formatMessage'];

export type CreditCheckFormValues<FormGenericType extends Kind = 'long'> = z.infer<
  ReturnType<typeof creditCheckFormSchema<FormGenericType>>
>;
